<template>
  <div class="bg-gray-50 min-h-screen">
    <!-- 全局悬浮组件 -->
    <SideFloat />

    <!-- 页面标题区 -->
    <section class="relative h-[400px] md:h-[500px] overflow-hidden">
      <img 
        src="@/assets/images/ContactUs.png" 
        alt="MLV Sound Insulation Felt Factory Panorama"
        class="w-full h-full object-cover"
      >
      <div class="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
        <div class="text-center text-white px-6">
          <h2 class="text-3xl md:text-5xl font-bold mb-4">Contact Us</h2>
          <p class="text-lg md:text-xl max-w-3xl mx-auto"> Have questions or need a custom solution? Our team is ready to help. We respond within 24 hours.</p>
        </div>
      </div>
    </section>

    <!-- 联系信息与表单区（改为上下布局，解决对齐问题） -->
    <section class="py-12">
      <div class="container mx-auto px-6">
        <!-- 先展示联系信息 -->
        <div class="bg-white p-8 rounded-lg shadow-md mb-8  mx-auto " style="padding: 10px;">
          <h2 class="text-2xl font-bold text-gray-800 mb-6">Our Information</h2>
          <ul class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <li class="flex items-start">
              <div class="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <i class="fa fa-map-marker text-xl"></i>
              </div>
              <div>
                <h3 class="font-medium text-gray-800 mb-1">Address</h3>
                <p class="text-gray-600">{{ set.address || 'No address provided' }}</p>
              </div>
            </li>
            <li class="flex items-start">
              <div class="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <i class="fa fa-phone text-xl"></i>
              </div>
              <div>
                <h3 class="font-medium text-gray-800 mb-1">Phone</h3>
                <p class="text-gray-600">{{ set.linkPhone || 'No phone number provided' }}</p>
              </div>
            </li>
            <li class="flex items-start">
              <div class="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <i class="fa fa-envelope text-xl"></i>
              </div>
              <div>
                <h3 class="font-medium text-gray-800 mb-1">Email</h3>
                <p class="text-gray-600">{{ set.email || 'No email provided' }}</p>
              </div>
            </li>
            <li class="flex items-start">
              <div class="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <i class="fa fa-clock-o text-xl"></i>
              </div>
              <div>
                <h3 class="font-medium text-gray-800 mb-1">Business Hours</h3>
                <p class="text-gray-600">{{ set.workTime || 'No business hours provided' }}</p>
              </div>
            </li>
          </ul>
        </div>

        <!-- 联系表单 -->
        <div class="bg-white p-8 rounded-lg shadow-md mx-auto"  style="padding: 16px;">
          <h2 class="text-2xl font-bold text-gray-800 mb-6">Send Us a Message</h2>
          <p class="text-gray-600 mb-8">
            Fill out the form below and we’ll get back to you as soon as possible. For urgent inquiries, please call us directly.
          </p>

          <form class="space-y-6" @submit.prevent="handleSubmit">
            <!-- 姓名输入框 + 下方提示 -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div class="space-y-2">
                <label for="fullName" class="block text-gray-700 mb-2 text-sm">Full Name <span class="text-red-500">*</span></label>
                <input 
                  type="text" 
                  id="fullName" 
                  class="w-full px-4 py-3 border rounded-md transition-colors"
                  :class="{'border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent': !formErrors.fullName, 
                          'border-red-500 focus:ring-red-200 focus:border-red-500': formErrors.fullName}"
                  placeholder="John Doe"
                  v-model="formData.nickName"
                  @blur="validateField('fullName')"
                  @input="clearError('fullName')"
                  :disabled="isSubmitting"
                >
                <!-- 下方错误提示文字 -->
                <p v-if="formErrors.fullName" class="text-red-500 text-xs mt-1">{{ formErrors.fullName }}</p>
              </div>

              <!-- 手机号输入框 + 下方提示 -->
              <div class="space-y-2">
                <label for="phone" class="block text-gray-700 mb-2 text-sm">Phone Number <span class="text-red-500">*</span></label>
                <input 
                  type="tel" 
                  id="phone" 
                  class="w-full px-4 py-3 border rounded-md transition-colors"
                  :class="{'border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent': !formErrors.phone, 
                          'border-red-500 focus:ring-red-200 focus:border-red-500': formErrors.phone}"
                  placeholder="+1-800-123-4567"
                  v-model="formData.linkPhone"
                  @blur="validateField('phone')"
                  @input="clearError('phone')"
                  :disabled="isSubmitting"
                >
                <p v-if="formErrors.phone" class="text-red-500 text-xs mt-1">{{ formErrors.phone }}</p>
              </div>
            </div>

            <!-- 邮箱输入框 + 下方提示 -->
            <div class="space-y-2">
              <label for="email" class="block text-gray-700 mb-2 text-sm">Email Address <span class="text-red-500">*</span></label>
              <input 
                type="email" 
                id="email" 
                class="w-full px-4 py-3 border rounded-md transition-colors"
                :class="{'border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent': !formErrors.email, 
                        'border-red-500 focus:ring-red-200 focus:border-red-500': formErrors.email}"
                placeholder="john@example.com"
                v-model="formData.email"
                @blur="validateField('email')"
                @input="clearError('email')"
                :disabled="isSubmitting"
              >
              <p v-if="formErrors.email" class="text-red-500 text-xs mt-1">{{ formErrors.email }}</p>
            </div>

            <!-- 消息输入框 + 下方提示 -->
            <div class="space-y-2">
              <label for="message" class="block text-gray-700 mb-2 text-sm">Your Message <span class="text-red-500">*</span></label>
              <textarea 
                id="message" 
                rows="5" 
                class="w-full px-4 py-3 border rounded-md transition-colors"
                :class="{'border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent': !formErrors.message, 
                        'border-red-500 focus:ring-red-200 focus:border-red-500': formErrors.message}"
                placeholder="Please describe your inquiry in detail..."
                v-model="formData.messageContent"
                @blur="validateField('message')"
                @input="clearError('message')"
                :disabled="isSubmitting"
              ></textarea>
              <p v-if="formErrors.message" class="text-red-500 text-xs mt-1">{{ formErrors.message }}</p>
            </div>

            <button 
              type="submit" 
              class="w-full bg-gray-700 text-white py-3 rounded-md hover:bg-gray-800 transition-colors flex items-center justify-center text-lg font-medium disabled:opacity-70 disabled:cursor-not-allowed"
              :disabled="isSubmitting"
            >
              <i v-if="isSubmitting" class="fa fa-spinner fa-spin mr-2"></i>
              Send Message <i class="fa fa-paper-plane ml-2"></i>
            </button>

            <p class="text-center text-gray-500 text-sm mt-4">
              We respect your privacy. Your information will not be shared with third parties.
            </p>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import SideFloat from '@/components/SideFloat.vue'
import { ref, reactive, onMounted } from 'vue'
import { indexSet } from '@/api/index'
import { messageSubmit } from '@/api/message' // 导入留言接口

const set = ref({})
onMounted(() => {
  getIndexSet()
})

// 获取站点配置
const getIndexSet = async () => {
  try {
    let res = await indexSet()
    set.value = res.data || {}
    console.log('站点配置:', set.value)
  } catch (error) {
    console.error('获取站点配置失败:', error)
    // ElMessage.error('Failed to load contact information')
  }
}

// 表单数据存储（对应接口字段）
const formData = reactive({
  nickName: '',     // 名字
  email: '',        // 邮箱
  linkPhone: '',    // 联系方式
  messageContent: ''// 留言内容
})

// 错误信息存储
const formErrors = reactive({
  fullName: '',
  phone: '',
  email: '',
  message: ''
})

// 提交状态
const isSubmitting = ref(false)

// 单个字段校验逻辑
const validateField = (field) => {
  switch (field) {
    case 'fullName':
      if (!formData.nickName.trim()) {
        formErrors.fullName = 'This field is required'
      } else if (formData.nickName.trim().length < 2) {
        formErrors.fullName = 'Please enter a valid full name'
      } else {
        formErrors.fullName = ''
      }
      break
    case 'phone':
      if (!formData.linkPhone.trim()) {
        formErrors.phone = 'This field is required'
      }else {
        formErrors.phone = ''
      }
      break
    case 'email':
      if (!formData.email.trim()) {
        formErrors.email = 'This field is required'
      }  else {
        formErrors.email = ''
      }
      break
    case 'message':
      if (!formData.messageContent.trim()) {
        formErrors.message = 'This field is required'
      } else if (formData.messageContent.trim().length < 10) {
        formErrors.message = 'Please provide more details (at least 10 characters)'
      } else {
        formErrors.message = ''
      }
      break
    default:
      break
  }
}

// 清除单个字段错误
const clearError = (field) => {
  formErrors[field] = ''
}

// 表单提交逻辑（对接真实接口）
const handleSubmit = async () => {
  // 先校验所有字段
   Object.keys(formErrors).forEach(field => validateField(field))
  
  // 检查是否有错误
  const hasError = Object.values(formErrors).some(error => error)
  if (!hasError) {
    try {
      isSubmitting.value = true
      // 调用真实留言接口
      const res = await messageSubmit(formData)
      
      // 接口返回成功处理
      if (res.code === 200 || res.success) {
        alert('Your message has been sent successfully! We will respond within 24 hours.')
        // 重置表单
        formData.nickName = ''
        formData.email = ''
        formData.linkPhone = ''
        formData.messageContent = ''
      } else {
        alert(res.msg || 'Failed to send message, please try again')
      }
    } catch (error) {
      console.error('表单提交失败:', error)
      alert('Sorry, there was an error submitting your form. Please try again later.')
    } finally {
      isSubmitting.value = false
    }
  }
}
</script>

<style scoped>
/* 输入框交互优化 */
input:focus, textarea:focus {
  box-shadow: 0 0 0 2px rgba(22, 93, 255, 0.2);
  outline: none;
}

/* 错误状态输入框焦点样式 */
input.border-red-500:focus, textarea.border-red-500:focus {
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

/* 按钮禁用状态样式 */
button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

/* 平滑过渡效果 */
* {
  transition: all 0.2s ease-in-out;
}

/* 响应式优化 */
@media (max-width: 768px) {
  .grid-cols-1 md\:grid-cols-2 {
    grid-template-columns: 1fr;
  }
  
  .bg-white.p-8 {
    padding: 6px;
  }
}
</style>